import React from "react";
import * as axios from "axios";
import {Button, Menu, MenuItem, Divider, TextField} from "@material-ui/core";
import "./styles.css";
import {
  DAY_NAMES, isShiftActiveOnDate, startOfWeek, formatDate, dateKey,
  STATUS_LABELS, STATUS_CLASSES, SHIFT_STATUS_KEYS,
  groupShiftChanges, shiftForDate, planShiftExchange, SHIFT_EXCHANGE_GROUP_PREFIX,
} from "./shiftUtils";
import {useCity} from "../../CityContext";

const baseUrl = 'https://api.appetito.ro/';

// 15-minute increments across a full day, e.g. "00:00", "00:15", ... "23:45".
const TIME_OPTIONS = Array.from({length: 24 * 4}, (_, i) => {
  const hours = Math.floor(i / 4);
  const minutes = (i % 4) * 15;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
});

// Statuses whose working hours are independently settable per day inline in
// the popover (unlike shift1/shift2/shift3, which just display the
// deliverer's default hours). A day with none of these statuses can still
// get a one-off hours override via "hours_change" — see the "Schimbă ora"
// menu item — it just doesn't get its own inline editor since it isn't
// otherwise changing what's displayed for that day.
const EDITABLE_HOURS_STATUSES = ['normal', 'extra', 'extra_paid'];

// "shift1"/"shift2"/"shift3" (STATUS_LABELS/STATUS_CLASSES/SHIFT_STATUS_KEYS,
// imported above) are computed on the fly from Deliverer.Shift, never
// stored. "normal"/"extra"/"extra_paid"/"vacation"/"swapped" are day-specific
// manual overrides persisted as DelivererScheduleEntry rows, and only ever
// affect the single date they're set on. covered_deliverer_id marks an
// entry's own deliverer as covering someone else's day — that other person
// is excluded from that one day's schedule — and survives regardless of
// which status the entry ends up with: a deliverer who took over someone
// else's Saturday can still be switched to "vacation"/"extra"/etc. on that
// same Saturday afterwards (e.g. they can no longer work it either) without
// bringing the person they were covering back onto the schedule. Any
// override entry (or a plain shift day with no override at all, via the
// "car_change" / "hours_change" statuses) can also carry a one-off car_id
// and/or work hours, overriding the deliverer's usual car/hours for that
// date only.
//
// Deliverers and cars follow the sidebar city dropdown (CityContext) via
// the cityIds prop, same as the dashboard (Components/Orders.js).
class Program extends React.Component {
  state = {
    users: [],
    cars: [],
    entries: [],
    shiftChangesByDeliverer: {},
    weekOffset: 0,
    dragOverKey: null,
    menuAnchor: null,
    menuTarget: null,
    swapFormOpen: false,
    swapCoverDelivererId: '',
    carFormOpen: false,
    carFormCarId: '',
    hoursFormOpen: false,
    hoursFormStart: '',
    hoursFormEnd: '',
    exchangeFormOpen: false,
    exchangePartnerId: '',
    exchangeSaving: false,
  };

  componentDidMount() {
    this.getDeliverers();
    this.getCars();
    this.getEntries();
    this.getShiftChanges();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.weekOffset !== this.state.weekOffset) {
      this.getEntries();
    }
    if ((prevProps.cityIds || []).join(',') !== (this.props.cityIds || []).join(',')) {
      this.closeStatusMenu();
      this.getDeliverers();
      this.getCars();
    }
  }

  // Comma-separated city ids for the backend's ?cityIds= query param.
  cityIdsParam = () => (this.props.cityIds || []).join(',');

  getDeliverers = async () => {
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/all-deliverers`, {
      params: {cityIds: this.cityIdsParam()},
      headers: {Authorization: `Bearer ${token}`},
    });
    this.setState({users: response.data});
  };

  getCars = async () => {
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/cars`, {
      params: {cityIds: this.cityIdsParam()},
      headers: {Authorization: `Bearer ${token}`},
    });
    this.setState({cars: response.data || []});
  };

  // entry.car_id (a one-off override for this single day, see "car_change")
  // takes priority over the deliverer's own default car.
  carPlateFor = (deliverer, entry) => {
    const carId = (entry && entry.car_id) || deliverer.car_id;
    if (!carId) {
      return null;
    }
    const car = this.state.cars.find((c) => c.id === carId);
    return car ? car.plate_number : null;
  };

  weekStart = () => {
    const start = startOfWeek(new Date());
    start.setDate(start.getDate() + this.state.weekOffset * 7);
    return start;
  };

  weekDates = () => {
    const start = this.weekStart();
    return Array.from({length: 7}, (_, d) => {
      const date = new Date(start);
      date.setDate(date.getDate() + d);
      return date;
    });
  };

  // Every deliverer's shift history — which shift someone is on is resolved
  // per day from this (see shiftForDate), so a permanent shift change only
  // shows from its effective date and past weeks keep the shift they had.
  getShiftChanges = async () => {
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/shift-changes`, {
      headers: {Authorization: `Bearer ${token}`},
    });
    this.setState({shiftChangesByDeliverer: groupShiftChanges(response.data)});
  };

  getEntries = async () => {
    const week = this.weekDates();
    const token = localStorage.getItem('access_token');
    const response = await axios.default.get(`${baseUrl}v1/admin/deliverer-schedule`, {
      params: {start: dateKey(week[0]), end: dateKey(week[6])},
      headers: {Authorization: `Bearer ${token}`},
    });
    this.setState({entries: response.data || []});
  };

  goToPreviousWeek = () => this.setState({weekOffset: this.state.weekOffset - 1});

  goToNextWeek = () => this.setState({weekOffset: this.state.weekOffset + 1});

  findEntry = (delivererId, date) => {
    const key = dateKey(date);
    return this.state.entries.find((e) => e.deliverer_id === delivererId && dateKey(new Date(e.date)) === key);
  };

  // Builds one day's list of {deliverer, status, entry, coveredDelivererId}.
  // Single pass: everyone with their own override entry for this date shows
  // as that entry's status (a "swapped" entry belongs to the COVERING
  // deliverer, entry.covered_deliverer_id names who they're covering);
  // everyone else shows on their normal computed shift, unless someone
  // else's "swapped" entry today covers them, in which case they're skipped
  // — the covering deliverer already showed up via their own entry. Nothing
  // here reads past days at all, so a swap only ever touches the one date it
  // was set on.
  //
  // A deleted deliverer only shows up on days before today — the historical
  // record of when they actually worked stays intact, but they drop out of
  // today/future scheduling the moment they're deleted (same as if their
  // shift were cleared), so nobody keeps auto-appearing on a rotation they
  // were removed from.
  //
  // Symmetric with that, but the other direction: u.first_order_at (set by
  // the backend from their actual first delivered order — there's no hire
  // date to go by) hides them from days *before* they started, but only
  // once those days are in the past — a brand new hire still needs to show
  // up on today/future days so they can be scheduled before that first
  // order ever happens. Empty/missing first_order_at (never delivered
  // anything yet) doesn't hide them at all, same "unknown isn't 'never
  // started'" rule the backend follows.
  //
  // A deleted deliverer's own cutoff doesn't stop at "today" either — their
  // Shift rotation (never cleared on deletion) would otherwise keep marking
  // them "working" on *every* past day forever, not just the ones right
  // around when they left. u.last_order_at (their last actual delivered
  // order) bounds that: a past day after it means they'd already stopped,
  // so they don't show. Same "unknown isn't never" rule when it's missing.
  computeDayEntries = (date, sortedUsers) => {
    const isPastDate = dateKey(date) < dateKey(new Date());
    const eligibleUsers = sortedUsers.filter((u) => {
      if (u.first_order_at && dateKey(date) < u.first_order_at && isPastDate) {
        return false;
      }
      if (!u.deleted) {
        return true;
      }
      if (!isPastDate) {
        return false;
      }
      return !u.last_order_at || dateKey(date) <= u.last_order_at;
    });

    const dayEntries = eligibleUsers.map((deliverer) => ({deliverer, entry: this.findEntry(deliverer.id, date)}));
    // covered_deliverer_id excludes that person regardless of the covering
    // entry's current status — see the comment above STATUS_LABELS.
    const coveredToday = new Set(
      dayEntries
        .filter(({entry}) => entry && entry.covered_deliverer_id)
        .map(({entry}) => entry.covered_deliverer_id)
    );

    const included = new Set();
    const results = [];
    const add = (deliverer, status, entry, coveredDelivererId = null) => {
      if (included.has(deliverer.id)) {
        return;
      }
      included.add(deliverer.id);
      results.push({deliverer, status, entry, coveredDelivererId});
    };

    dayEntries.forEach(({deliverer, entry}) => {
      // "car_change" / "hours_change" carry only a one-off override — the
      // shift status shown is still the deliverer's own normal computed
      // shift.
      if (entry && entry.status !== 'car_change' && entry.status !== 'hours_change') {
        add(deliverer, entry.status, entry, entry.covered_deliverer_id || null);
        return;
      }
      if (coveredToday.has(deliverer.id)) {
        return;
      }
      const shift = shiftForDate(deliverer, date, this.state.shiftChangesByDeliverer);
      if (isShiftActiveOnDate(shift, date)) {
        add(deliverer, SHIFT_STATUS_KEYS[shift], entry || null);
      }
    });

    return results;
  };

  upsertEntry = async (delivererId, date, status, workStart = '', workEnd = '', coveredDelivererId = 0, carId = 0) => {
    const token = localStorage.getItem('access_token');
    const response = await axios.default.post(`${baseUrl}v1/admin/deliverer-schedule`, {
      deliverer_id: delivererId,
      date: dateKey(date),
      status,
      work_start: workStart,
      work_end: workEnd,
      covered_deliverer_id: coveredDelivererId,
      car_id: carId,
    }, {headers: {Authorization: `Bearer ${token}`}});
    const key = dateKey(date);
    this.setState({
      entries: [
        ...this.state.entries.filter((e) => !(e.deliverer_id === delivererId && dateKey(new Date(e.date)) === key)),
        response.data,
      ],
    });
    return response.data;
  };

  deleteEntry = async (entryId) => {
    const token = localStorage.getItem('access_token');
    await axios.default.delete(`${baseUrl}v1/admin/deliverer-schedule/${entryId}`, {
      headers: {Authorization: `Bearer ${token}`},
    });
    this.setState({entries: this.state.entries.filter((e) => e.id !== entryId)});
  };

  handleDragStart = (event, delivererId) => {
    event.dataTransfer.setData('text/plain', String(delivererId));
  };

  handleDragOver = (event, key) => {
    event.preventDefault();
    if (this.state.dragOverKey !== key) {
      this.setState({dragOverKey: key});
    }
  };

  handleDrop = (event, date) => {
    event.preventDefault();
    this.setState({dragOverKey: null});
    const delivererId = Number(event.dataTransfer.getData('text/plain'));
    if (!delivererId) {
      return;
    }
    this.upsertEntry(delivererId, date, 'extra');
  };

  openStatusMenu = (event, deliverer, date, status, entry, coveredDelivererId) => {
    this.setState({
      menuAnchor: event.currentTarget,
      menuTarget: {deliverer, date, status, entry, coveredDelivererId},
      swapFormOpen: false,
      swapCoverDelivererId: '',
      carFormOpen: false,
      carFormCarId: '',
      hoursFormOpen: false,
      hoursFormStart: '',
      hoursFormEnd: '',
      exchangeFormOpen: false,
      exchangePartnerId: '',
    });
  };

  closeStatusMenu = () => this.setState({
    menuAnchor: null,
    menuTarget: null,
    swapFormOpen: false,
    swapCoverDelivererId: '',
    carFormOpen: false,
    carFormCarId: '',
    hoursFormOpen: false,
    hoursFormStart: '',
    hoursFormEnd: '',
    exchangeFormOpen: false,
    exchangePartnerId: '',
  });

  openSwapForm = () => {
    this.setState({swapFormOpen: true, swapCoverDelivererId: ''});
  };

  closeSwapForm = () => this.setState({swapFormOpen: false, swapCoverDelivererId: ''});

  openCarForm = () => {
    const {entry} = this.state.menuTarget;
    this.setState({carFormOpen: true, carFormCarId: (entry && entry.car_id) || ''});
  };

  closeCarForm = () => this.setState({carFormOpen: false, carFormCarId: ''});

  openHoursForm = () => {
    const {entry, deliverer} = this.state.menuTarget;
    const start = (entry && entry.work_start) || deliverer.work_start || '';
    const end = (entry && entry.work_end) || deliverer.work_end || '';
    this.setState({hoursFormOpen: true, hoursFormStart: start, hoursFormEnd: end});
  };

  closeHoursForm = () => this.setState({hoursFormOpen: false, hoursFormStart: '', hoursFormEnd: ''});

  openExchangeForm = () => this.setState({exchangeFormOpen: true, exchangePartnerId: ''});

  closeExchangeForm = () => this.setState({exchangeFormOpen: false, exchangePartnerId: ''});

  // Permanent shift-1 <-> shift-2 trade with the 3-days-each transition (see
  // models.ShiftExchangePlan), starting with the 2-day block menuTarget.date
  // is in. The backend writes the transition-day swaps and both future-dated
  // shift changes, so both entries and shift history get reloaded.
  confirmExchange = async () => {
    const {exchangePartnerId, menuTarget} = this.state;
    if (!exchangePartnerId) {
      return;
    }
    const token = localStorage.getItem('access_token');
    this.setState({exchangeSaving: true});
    try {
      await axios.default.post(`${baseUrl}v1/admin/shift-exchange`, {
        deliverer_id: menuTarget.deliverer.id,
        partner_deliverer_id: Number(exchangePartnerId),
        date: dateKey(menuTarget.date),
      }, {headers: {Authorization: `Bearer ${token}`}});
      await Promise.all([this.getEntries(), this.getShiftChanges()]);
      this.closeStatusMenu();
    } catch (error) {
      alert(error?.response?.data?.message || error?.response?.data?.error || 'Eroare la schimbarea turei.');
    } finally {
      this.setState({exchangeSaving: false});
    }
  };

  cancelExchange = async () => {
    const {entry} = this.state.menuTarget;
    if (!window.confirm('Anulezi schimbarea permanentă de tură pentru ambii livratori?')) {
      return;
    }
    const token = localStorage.getItem('access_token');
    try {
      await axios.default.delete(`${baseUrl}v1/admin/shift-exchange/${entry.swap_group_id}`, {
        headers: {Authorization: `Bearer ${token}`},
      });
      await Promise.all([this.getEntries(), this.getShiftChanges()]);
      this.closeStatusMenu();
    } catch (error) {
      alert(error?.response?.data?.message || error?.response?.data?.error || 'Eroare la anularea schimbării de tură.');
    }
  };

  // Day-only takeover: the picked deliverer covers menuTarget.deliverer's
  // shift for menuTarget.date alone — nothing else changes, on any other
  // day. See DelivererScheduleEntry.CoveredDelivererID.
  confirmSwap = async () => {
    const {swapCoverDelivererId, menuTarget} = this.state;
    if (!swapCoverDelivererId) {
      return;
    }
    await this.upsertEntry(Number(swapCoverDelivererId), menuTarget.date, 'swapped', '', '', menuTarget.deliverer.id);
    this.closeStatusMenu();
  };

  // Sets a one-off car for menuTarget.date only — keeps whatever status
  // (or lack of one) already applies that day, just adds/replaces car_id.
  confirmCarChange = async () => {
    const {carFormCarId, menuTarget} = this.state;
    if (!carFormCarId) {
      return;
    }
    const {deliverer, date, entry} = menuTarget;
    const targetStatus = entry ? entry.status : 'car_change';
    const updated = await this.upsertEntry(
      deliverer.id, date, targetStatus,
      entry?.work_start || '', entry?.work_end || '', entry?.covered_deliverer_id || 0, Number(carFormCarId)
    );
    this.setState({
      menuTarget: {...menuTarget, status: targetStatus, entry: updated},
      carFormOpen: false,
      carFormCarId: '',
    });
  };

  // Sets one-off hours for menuTarget.date only — keeps whatever status (or
  // lack of one) already applies that day, just adds/replaces work hours.
  confirmHoursChange = async () => {
    const {hoursFormStart, hoursFormEnd, menuTarget} = this.state;
    if (!hoursFormStart || !hoursFormEnd) {
      return;
    }
    const {deliverer, date, entry} = menuTarget;
    const targetStatus = entry ? entry.status : 'hours_change';
    const updated = await this.upsertEntry(
      deliverer.id, date, targetStatus,
      hoursFormStart, hoursFormEnd, entry?.covered_deliverer_id || 0, entry?.car_id || 0
    );
    this.setState({
      menuTarget: {...menuTarget, status: targetStatus, entry: updated},
      hoursFormOpen: false,
      hoursFormStart: '',
      hoursFormEnd: '',
    });
  };

  // Switches status but keeps the popover open (rather than closing, like
  // before) so extra/extra_paid can immediately get their working hours set
  // right after picking them, without reopening the popover. Keeps any car
  // override already set on this day, and keeps covered_deliverer_id if this
  // deliverer was covering someone else's day — e.g. flipping a "swapped"
  // entry to "vacation" (they can't cover it after all) must not bring the
  // covered deliverer back onto the schedule; see the comment above
  // STATUS_LABELS.
  chooseStatus = async (status) => {
    const {deliverer, date, entry} = this.state.menuTarget;
    const updated = await this.upsertEntry(
      deliverer.id, date, status, entry?.work_start || '', entry?.work_end || '',
      entry?.covered_deliverer_id || 0, entry?.car_id || 0
    );
    this.setState({menuTarget: {...this.state.menuTarget, status, entry: updated}});
  };

  updateEntryHours = async (field, value) => {
    const {deliverer, date, status, entry} = this.state.menuTarget;
    const workStart = field === 'work_start' ? value : (entry?.work_start || '');
    const workEnd = field === 'work_end' ? value : (entry?.work_end || '');
    const updated = await this.upsertEntry(
      deliverer.id, date, status, workStart, workEnd, entry?.covered_deliverer_id || 0, entry?.car_id || 0
    );
    this.setState({menuTarget: {...this.state.menuTarget, entry: updated}});
  };

  // Deleting the entry always reverts the whole day back to normal —
  // whatever status and/or car override it carried, both scoped to this one
  // date, are gone.
  revertStatus = async () => {
    const {entry} = this.state.menuTarget;
    if (entry) {
      await this.deleteEntry(entry.id);
    }
    this.closeStatusMenu();
  };

  // Clears only the car override for this day, keeping the entry's status
  // (extra/vacation/swapped) intact — unless the entry exists solely to
  // carry a car override ("car_change"), in which case there's nothing left
  // to keep and the whole entry is removed.
  revertCar = async () => {
    const {deliverer, date, entry} = this.state.menuTarget;
    if (!entry) {
      return;
    }
    if (entry.status === 'car_change') {
      await this.deleteEntry(entry.id);
    } else {
      await this.upsertEntry(
        deliverer.id, date, entry.status, entry.work_start || '', entry.work_end || '', entry.covered_deliverer_id || 0, 0
      );
    }
    this.closeStatusMenu();
  };

  // Clears only the one-off hours override for this day, keeping the
  // entry's status (extra/vacation/swapped/car_change) intact — unless the
  // entry exists solely to carry an hours override ("hours_change"), in
  // which case there's nothing left to keep and the whole entry is removed.
  revertHours = async () => {
    const {deliverer, date, entry} = this.state.menuTarget;
    if (!entry) {
      return;
    }
    if (entry.status === 'hours_change') {
      await this.deleteEntry(entry.id);
    } else {
      await this.upsertEntry(
        deliverer.id, date, entry.status, '', '', entry.covered_deliverer_id || 0, entry.car_id || 0
      );
    }
    this.closeStatusMenu();
  };

  render() {
    const allUsers = this.state.users || [];
    const users = allUsers.filter((u) => !u.deleted);
    const {
      entries, dragOverKey, menuAnchor, menuTarget, swapFormOpen, swapCoverDelivererId, carFormOpen, carFormCarId,
      hoursFormOpen, hoursFormStart, hoursFormEnd, exchangeFormOpen, exchangePartnerId, exchangeSaving,
      shiftChangesByDeliverer,
    } = this.state;
    const cars = this.state.cars || [];
    const activeCars = cars.filter((c) => !c.deleted);
    const week = this.weekDates();
    const today = dateKey(new Date());

    // computeDayEntries gets the full roster, including deleted deliverers
    // — it only lets a deleted one appear on days before today (their
    // historical record), never on today or later. Everything else here
    // (pills tray, swap-cover picker) stays active-only, since you can't
    // hand new work to someone who's been deleted.
    const sortedAllUsers = [...allUsers].sort((a, b) => a.shift - b.shift || a.first_name.localeCompare(b.first_name));
    const hasAnySchedule = users.some((u) => u.shift !== 0) || entries.length > 0;

    // Permanent shift exchange: only offered for a shift-1/shift-2 deliverer,
    // with partners on the other rotating shift on that date.
    const menuShift = menuTarget ? shiftForDate(menuTarget.deliverer, menuTarget.date, shiftChangesByDeliverer) : 0;
    const canExchange = menuShift === 1 || menuShift === 2;
    const exchangePartners = canExchange
      ? users.filter((u) => u.id !== menuTarget.deliverer.id
        && shiftForDate(u, menuTarget.date, shiftChangesByDeliverer) === 3 - menuShift)
      : [];
    const exchangePartner = exchangePartnerId ? users.find((u) => u.id === Number(exchangePartnerId)) : null;
    const exchangePlan = exchangePartner
      ? planShiftExchange(menuTarget.deliverer, exchangePartner, menuTarget.date, shiftChangesByDeliverer)
      : null;
    const formatDays = (days) => days.map((d) => formatDate(d).slice(0, 5)).join(', ');
    const isExchangeEntry = !!(menuTarget && menuTarget.entry && menuTarget.entry.swap_group_id
      && menuTarget.entry.swap_group_id.startsWith(SHIFT_EXCHANGE_GROUP_PREFIX));

    return (
      <div className="deliverers-page">
        <div className="deliverers-section">
          <h3 className="deliverers-section-title">Livratori</h3>
          <div className="deliverers-pills">
            {users.length === 0 ? (
              <div className="deliverers-empty-state">Niciun livrator.</div>
            ) : users.map((u) => (
              <span
                key={u.id}
                className="deliverers-pill"
                draggable
                onDragStart={(event) => this.handleDragStart(event, u.id)}
              >
                {u.first_name} {u.last_name}
              </span>
            ))}
          </div>
        </div>

        <div className="deliverers-section">
          <div className="deliverers-section-header">
            <h3 className="deliverers-section-title">Planificare ture</h3>
            <div className="deliverers-shift-legend">
              <span className="deliverers-shift-legend-item"><span className="deliverers-shift-swatch shift-1"/> Tura 1</span>
              <span className="deliverers-shift-legend-item"><span className="deliverers-shift-swatch shift-2"/> Tura 2</span>
              <span className="deliverers-shift-legend-item"><span className="deliverers-shift-swatch shift-3"/> MieJoiVin</span>
              <span className="deliverers-shift-legend-item"><span className="deliverers-shift-swatch status-normal"/> Program normal</span>
              <span className="deliverers-shift-legend-item"><span className="deliverers-shift-swatch status-extra"/> Extra</span>
              <span className="deliverers-shift-legend-item"><span className="deliverers-shift-swatch status-extra-paid"/> Extra (plătită)</span>
              <span className="deliverers-shift-legend-item"><span className="deliverers-shift-swatch status-vacation"/> Concediu</span>
              <span className="deliverers-shift-legend-item"><span className="deliverers-shift-swatch status-swapped"/> Schimb tură</span>
            </div>
          </div>

          <div className="deliverers-scheduler-nav">
            <Button onClick={this.goToPreviousWeek}>&lt; Săptămâna trecută</Button>
            <span className="deliverers-scheduler-week-label">{formatDate(week[0])} – {formatDate(week[6])}</span>
            <Button onClick={this.goToNextWeek}>Săptămâna următoare &gt;</Button>
          </div>

          {!hasAnySchedule ? (
            <div className="deliverers-empty-state">Niciun livrator programat această săptămână. Trage un livrator din listă pe o zi pentru a-l adăuga.</div>
          ) : (
            <div className="deliverers-scheduler-board">
              {week.map((date) => {
                const key = dateKey(date);
                const dayEntries = this.computeDayEntries(date, sortedAllUsers);
                return (
                  <div
                    key={key}
                    className={`deliverers-scheduler-day-column${dragOverKey === key ? ' drag-over' : ''}${key === today ? ' is-today' : ''}`}
                    onDragOver={(event) => this.handleDragOver(event, key)}
                    onDragLeave={() => this.setState({dragOverKey: null})}
                    onDrop={(event) => this.handleDrop(event, date)}
                  >
                    <div className="deliverers-scheduler-day-header">
                      <div className="deliverers-scheduler-day-name">{DAY_NAMES[date.getDay()]}</div>
                      <div className="deliverers-scheduler-day-badge">
                        {formatDate(date)}
                      </div>
                    </div>
                    <div className="deliverers-scheduler-day-body">
                      {dayEntries.length === 0 && <div className="deliverers-scheduler-day-empty">—</div>}
                      {dayEntries.map(({deliverer, status, entry, coveredDelivererId}) => {
                        // entry.work_start/work_end wins whenever set, regardless of
                        // status — a "swapped" entry can carry a one-off hours
                        // override the same way "hours_change" does (see "Schimbă
                        // ora"), and previously only normal/extra/extra_paid/
                        // hours_change were shown here, so setting hours on a
                        // covering deliverer's swapped day silently had no visible
                        // effect: the cell kept showing their own default hours.
                        const hours = (entry && entry.work_start && entry.work_end)
                          ? `${entry.work_start} - ${entry.work_end}`
                          : (deliverer.work_start && deliverer.work_end ? `${deliverer.work_start} - ${deliverer.work_end}` : null);
                        const plate = this.carPlateFor(deliverer, entry);
                        return (
                          <span
                            key={deliverer.id}
                            className={`deliverers-scheduler-status ${STATUS_CLASSES[status]}`}
                            onClick={(event) => this.openStatusMenu(event, deliverer, date, status, entry, coveredDelivererId)}
                            title={STATUS_LABELS[status]}
                          >
                            <span className="deliverers-scheduler-status-name">{deliverer.first_name} {deliverer.last_name}</span>
                            {hours && <span className="deliverers-scheduler-status-hours">{hours}</span>}
                            {plate && <span className="deliverers-scheduler-status-hours">{plate}</span>}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={this.closeStatusMenu}>
          {menuTarget && (
            <div className="deliverers-status-menu-header">
              {menuTarget.deliverer.first_name} {menuTarget.deliverer.last_name} — {formatDate(menuTarget.date)}
            </div>
          )}
          <MenuItem onClick={() => this.chooseStatus('vacation')} selected={menuTarget && menuTarget.status === 'vacation'}>
            Concediu
          </MenuItem>
          <MenuItem onClick={() => this.chooseStatus('normal')} selected={menuTarget && menuTarget.status === 'normal'}>
            Program normal
          </MenuItem>
          <MenuItem onClick={() => this.chooseStatus('extra')} selected={menuTarget && menuTarget.status === 'extra'}>
            Extra
          </MenuItem>
          <MenuItem onClick={() => this.chooseStatus('extra_paid')} selected={menuTarget && menuTarget.status === 'extra_paid'}>
            Extra (plătită)
          </MenuItem>
          <MenuItem onClick={this.openSwapForm} selected={menuTarget && menuTarget.status === 'swapped'}>
            Schimb tură
          </MenuItem>
          {swapFormOpen && menuTarget && (
            <div className="deliverers-status-hours">
              <div className="deliverers-status-hours-label">
                Cine preia tura lui {menuTarget.deliverer.first_name} pe {formatDate(menuTarget.date)}?
              </div>
              <TextField
                select
                value={swapCoverDelivererId}
                onChange={(event) => this.setState({swapCoverDelivererId: event.target.value})}
                margin="dense"
                fullWidth
                style={{minWidth: 200}}
              >
                <MenuItem value="">Alege livrator</MenuItem>
                {users.filter((u) => u.id !== menuTarget.deliverer.id).map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</MenuItem>
                ))}
              </TextField>
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8}}>
                <Button size="small" onClick={this.closeSwapForm}>Anulează</Button>
                <Button size="small" color="primary" disabled={!swapCoverDelivererId} onClick={this.confirmSwap}>
                  Confirmă schimbul
                </Button>
              </div>
            </div>
          )}
          {canExchange && (
            <MenuItem onClick={this.openExchangeForm}>
              Schimbă tura permanent
            </MenuItem>
          )}
          {exchangeFormOpen && menuTarget && (
            <div className="deliverers-status-hours" style={{maxWidth: 320}}>
              <div className="deliverers-status-hours-label">
                Cu cine face schimb de tură {menuTarget.deliverer.first_name} (Tura {menuShift} → Tura {3 - menuShift})?
              </div>
              <TextField
                select
                value={exchangePartnerId}
                onChange={(event) => this.setState({exchangePartnerId: event.target.value})}
                margin="dense"
                fullWidth
                style={{minWidth: 200}}
              >
                <MenuItem value="">Alege livrator</MenuItem>
                {exchangePartners.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</MenuItem>
                ))}
              </TextField>
              {exchangePlan && (
                <div className="deliverers-status-hours-label" style={{whiteSpace: 'normal', marginTop: 8}}>
                  {exchangePlan.first.first_name} lucrează {formatDays(exchangePlan.firstDays)}.<br/>
                  {exchangePlan.second.first_name} lucrează {formatDays(exchangePlan.secondDays)}.<br/>
                  Din {formatDate(exchangePlan.effectiveFrom)}: {exchangePlan.first.first_name} pe Tura {exchangePlan.firstNewShift},
                  {' '}{exchangePlan.second.first_name} pe Tura {exchangePlan.secondNewShift}.
                </div>
              )}
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8}}>
                <Button size="small" onClick={this.closeExchangeForm}>Anulează</Button>
                <Button size="small" color="primary" disabled={!exchangePlan || exchangeSaving} onClick={this.confirmExchange}>
                  Confirmă schimbarea
                </Button>
              </div>
            </div>
          )}
          <MenuItem onClick={this.openCarForm} selected={menuTarget && !!(menuTarget.entry && menuTarget.entry.car_id)}>
            Schimbă mașina (doar {menuTarget && formatDate(menuTarget.date)})
          </MenuItem>
          {carFormOpen && menuTarget && (
            <div className="deliverers-status-hours">
              <div className="deliverers-status-hours-label">
                Ce mașină folosește {menuTarget.deliverer.first_name} pe {formatDate(menuTarget.date)}?
              </div>
              <TextField
                select
                value={carFormCarId}
                onChange={(event) => this.setState({carFormCarId: event.target.value})}
                margin="dense"
                fullWidth
                style={{minWidth: 200}}
              >
                <MenuItem value="">Alege mașină</MenuItem>
                {activeCars.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.make} {c.model} — {c.plate_number}</MenuItem>
                ))}
              </TextField>
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8}}>
                <Button size="small" onClick={this.closeCarForm}>Anulează</Button>
                <Button size="small" color="primary" disabled={!carFormCarId} onClick={this.confirmCarChange}>
                  Confirmă mașina
                </Button>
              </div>
            </div>
          )}
          {menuTarget && EDITABLE_HOURS_STATUSES.includes(menuTarget.status) && (
            <div className="deliverers-status-hours">
              <div className="deliverers-status-hours-label">Interval orar</div>
              <div className="deliverers-status-hours-row">
                <TextField
                  select
                  label="Start"
                  value={(menuTarget.entry && menuTarget.entry.work_start) || ''}
                  onChange={(event) => this.updateEntryHours('work_start', event.target.value)}
                  margin="dense"
                  style={{minWidth: 90}}
                >
                  <MenuItem value="">-</MenuItem>
                  {TIME_OPTIONS.map((time) => <MenuItem key={time} value={time}>{time}</MenuItem>)}
                </TextField>
                <TextField
                  select
                  label="Sfârșit"
                  value={(menuTarget.entry && menuTarget.entry.work_end) || ''}
                  onChange={(event) => this.updateEntryHours('work_end', event.target.value)}
                  margin="dense"
                  style={{minWidth: 90}}
                >
                  <MenuItem value="">-</MenuItem>
                  {TIME_OPTIONS.map((time) => <MenuItem key={time} value={time}>{time}</MenuItem>)}
                </TextField>
              </div>
            </div>
          )}
          {menuTarget && !EDITABLE_HOURS_STATUSES.includes(menuTarget.status) && (
            <MenuItem onClick={this.openHoursForm} selected={!!(menuTarget.entry && menuTarget.entry.work_start)}>
              Schimbă ora (doar {formatDate(menuTarget.date)})
            </MenuItem>
          )}
          {hoursFormOpen && menuTarget && (
            <div className="deliverers-status-hours">
              <div className="deliverers-status-hours-label">
                Ce interval orar are {menuTarget.deliverer.first_name} pe {formatDate(menuTarget.date)}?
              </div>
              <div className="deliverers-status-hours-row">
                <TextField
                  select
                  label="Start"
                  value={hoursFormStart}
                  onChange={(event) => this.setState({hoursFormStart: event.target.value})}
                  margin="dense"
                  style={{minWidth: 90}}
                >
                  <MenuItem value="">-</MenuItem>
                  {TIME_OPTIONS.map((time) => <MenuItem key={time} value={time}>{time}</MenuItem>)}
                </TextField>
                <TextField
                  select
                  label="Sfârșit"
                  value={hoursFormEnd}
                  onChange={(event) => this.setState({hoursFormEnd: event.target.value})}
                  margin="dense"
                  style={{minWidth: 90}}
                >
                  <MenuItem value="">-</MenuItem>
                  {TIME_OPTIONS.map((time) => <MenuItem key={time} value={time}>{time}</MenuItem>)}
                </TextField>
              </div>
              <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8}}>
                <Button size="small" onClick={this.closeHoursForm}>Anulează</Button>
                <Button size="small" color="primary" disabled={!hoursFormStart || !hoursFormEnd} onClick={this.confirmHoursChange}>
                  Confirmă ora
                </Button>
              </div>
            </div>
          )}
          <Divider/>
          <MenuItem onClick={this.revertCar} disabled={!(menuTarget && menuTarget.entry && menuTarget.entry.car_id)}>
            Revino la mașina normală
          </MenuItem>
          <MenuItem
            onClick={this.revertHours}
            disabled={!(menuTarget && !EDITABLE_HOURS_STATUSES.includes(menuTarget.status) && menuTarget.entry && menuTarget.entry.work_start)}
          >
            Revino la ora normală
          </MenuItem>
          {isExchangeEntry ? (
            <MenuItem onClick={this.cancelExchange}>
              Anulează schimbarea permanentă de tură
            </MenuItem>
          ) : (
            <MenuItem onClick={this.revertStatus} disabled={!(menuTarget && menuTarget.entry)}>
              Revino la normal
            </MenuItem>
          )}
        </Menu>
      </div>
    )
  }
}

export default function ProgramPage() {
  const {selectedCityIds} = useCity();

  return (
    <Program cityIds={selectedCityIds}/>
  );
}
